import React from 'react';
import { PS } from '../theme';
import { fmt } from './format';
import { Icon } from './icons';

const AVATAR_PALETTE = [
  ['#c3a178', '#4a3a28'], ['#8a9a7b', '#2e3828'], ['#b08468', '#3d2820'],
  ['#a88c6e', '#352a1e'], ['#9b8574', '#2a2018'], ['#7d8a7f', '#263028'],
  ['#c4a584', '#3f2e1f'], ['#8b7d94', '#2a2232'],
];

export function Avatar({ name = '?', size = 32, style = {} }) {
  const initials = (name || '?').split(/\s+/).slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || '?';
  const hash = [...(name || '?')].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
  const [bg, fg] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg, color: fg,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: PS.fontUI, fontWeight: 600, fontSize: size * 0.38,
      letterSpacing: 0.2, flexShrink: 0, ...style,
    }}>{initials}</div>
  );
}

export function VerifiedMark({ size = 14, tone = 'green' }) {
  const fill = tone === 'green' ? '#3a7d5c' : PS.ink;
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" style={{ flexShrink: 0 }}>
      <path d="M7 0.8 L9.2 2 L11.8 1.8 L12.2 4.4 L13.5 6.5 L12.2 8.6 L11.8 11.2 L9.2 11 L7 12.2 L4.8 11 L2.2 11.2 L1.8 8.6 L0.5 6.5 L1.8 4.4 L2.2 1.8 L4.8 2 Z" fill={fill} />
      <path d="M4.5 7 L6.3 8.6 L9.6 5.3" stroke="#fff" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function DemoBadge({ size = 'sm', style = {} }) {
  const sm = size === 'sm';
  return (
    <span data-testid="demo-badge" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: sm ? '3px 8px' : '4px 11px', borderRadius: 999,
      background: 'oklch(0.64 0.16 48)', color: '#fff',
      border: 'none', fontFamily: PS.fontUI,
      fontSize: sm ? 10 : 11.5, fontWeight: 700, letterSpacing: 0.5,
      whiteSpace: 'nowrap', textTransform: 'uppercase',
      boxShadow: '0 1px 3px oklch(0.64 0.16 48 / 0.4)', ...style,
    }}>
      <span style={{ width: sm ? 5 : 6, height: sm ? 5 : 6, borderRadius: '50%', background: '#fff', opacity: 0.9 }} />
      ตัวอย่าง · DEMO
    </span>
  );
}


// 🔒 วงส่วนตัว / 🌐 วงสาธารณะ — shown wherever a circle name appears.
export function VisibilityBadge({ visibility = 'private', size = 'sm', onLight = true, style = {} }) {
  const isPublic = visibility === 'public';
  const sm = size === 'sm';
  const ico = sm ? 11 : 13;
  const label = isPublic ? 'วงสาธารณะ' : 'วงส่วนตัว';
  // public = terracotta accent; private = neutral/soft
  const bg = isPublic
    ? (onLight ? 'oklch(0.62 0.12 38 / 0.12)' : 'rgba(245,241,234,0.14)')
    : (onLight ? PS.panel : 'rgba(245,241,234,0.14)');
  const fg = isPublic ? (onLight ? 'oklch(0.5 0.13 38)' : PS.page) : (onLight ? PS.textMuted : PS.page);
  return (
    <span data-testid={`visibility-badge-${visibility}`} title={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: sm ? '3px 8px' : '4px 10px', borderRadius: 999,
      background: bg, color: fg, fontFamily: PS.fontUI,
      fontSize: sm ? 10.5 : 11.5, fontWeight: 600, whiteSpace: 'nowrap', ...style,
    }}>
      {isPublic ? <Icon.map size={ico} stroke={fg} /> : <Icon.lock size={ico} stroke={fg} />}
      {label}
    </span>
  );
}


export function Chip({ children, tone = 'default', style = {}, ...rest }) {
  const tones = {
    default: { bg: 'rgba(32,28,24,0.06)', fg: PS.text, bd: 'transparent' },
    outline: { bg: 'transparent', fg: PS.text, bd: PS.line },
    ink: { bg: PS.ink, fg: '#f5f1ea', bd: 'transparent' },
    terra: { bg: 'oklch(0.62 0.12 38 / 0.12)', fg: 'oklch(0.42 0.12 38)', bd: 'transparent' },
    green: { bg: 'oklch(0.78 0.06 155 / 0.35)', fg: 'oklch(0.38 0.08 155)', bd: 'transparent' },
    warn: { bg: 'oklch(0.88 0.08 82 / 0.4)', fg: 'oklch(0.40 0.10 60)', bd: 'transparent' },
  };
  const t = tones[tone] || tones.default;
  return (
    <span {...rest} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999,
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      fontFamily: PS.fontUI, fontSize: 11.5, fontWeight: 500, letterSpacing: 0.1, whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

export function Progress({ value = 0, max = 100, height = 4, color = PS.ink, track = PS.line }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div style={{ height, background: track, borderRadius: 999, overflow: 'hidden' }}>
      <div style={{ width: pct + '%', height: '100%', background: color, transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)' }} />
    </div>
  );
}

export function CreditMeter({ score = 720, max = 850, size = 120 }) {
  const pct = Math.max(0, Math.min(1, score / max));
  const r = size / 2 - 8, cx = size / 2, cy = size / 2;
  const startA = Math.PI, endA = 2 * Math.PI;
  const curA = startA + (endA - startA) * pct;
  const pt = (a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  const [x1, y1] = pt(startA), [x2, y2] = pt(curA), [x3, y3] = pt(endA);
  const large = pct > 0.5 ? 1 : 0;
  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 12 }}>
      <svg width={size} height={size / 2 + 12} viewBox={`0 0 ${size} ${size / 2 + 12}`}>
        <path d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x3} ${y3}`} stroke={PS.line} strokeWidth="5" fill="none" strokeLinecap="round" />
        <path d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`} stroke={PS.ink} strokeWidth="5" fill="none" strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', left: 0, right: 0, top: size / 2 - 22, textAlign: 'center' }}>
        <div className="num" style={{ fontSize: 28, fontWeight: 500, color: PS.text, lineHeight: 1 }}>{score}</div>
        <div style={{ fontFamily: PS.fontUI, fontSize: 10, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 3 }}>/ {max}</div>
      </div>
    </div>
  );
}

export function MiniStat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 9.5, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>{label}</div>
      <div className="num" style={{ fontSize: 13, fontWeight: 500, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export function Wordmark({ color = PS.ink, size = 18 }) {
  return (
    <div className="num" style={{ display: 'inline-flex', alignItems: 'baseline', gap: 1, fontWeight: 500, fontSize: size, color, letterSpacing: -0.4 }}>
      peershare<span style={{ color: 'oklch(0.62 0.12 38)', fontSize: size * 0.5 }}>●</span>
      <span style={{ fontFamily: "'IBM Plex Sans Thai', sans-serif", fontSize: size * 0.82, fontWeight: 600, letterSpacing: 0, marginLeft: size * 0.28 }}>เปียแชร์</span>
    </div>
  );
}

export { fmt };
